'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

interface CookieBannerProps {
  locale: string;
}

export default function CookieBanner({ locale }: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const t = useTranslations('cookieBanner');

  useEffect(() => {
    // Check if user has already seen the banner
    const cookieInfoSeen = localStorage.getItem('trink_cookie_info_seen');
    if (!cookieInfoSeen) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('trink_cookie_info_seen', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg 
              className="w-5 h-5 text-blue-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-900">
                {t('title')}
              </span>
              <span className="text-gray-600">
                {t('description')}
              </span>
              <div className="flex gap-2">
                <Link 
                  href={`/${locale}/bilgi?sozlesme=cerez-politikasi`}
                  className="text-blue-600 hover:text-blue-800 underline text-xs"
                >
                  {t('cookiePolicy')}
                </Link>
                <span className="text-gray-400">•</span>
                <Link 
                  href={`/${locale}/bilgi?sozlesme=kvkk-aydinlatma-metni`}
                  className="text-blue-600 hover:text-blue-800 underline text-xs"
                >
                  {t('privacyPolicy')}
                </Link>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Banner'ı kapat"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg 
                className="w-4 h-4 text-blue-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="font-medium text-gray-900">
                    {t('title')}
                  </span>
                  <span className="text-gray-600 ml-1">
                    {t('description')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <Link 
                    href={`/${locale}/bilgi?sozlesme=cerez-politikasi`}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {t('cookiePolicy')}
                  </Link>
                  <Link 
                    href={`/${locale}/bilgi?sozlesme=kvkk-aydinlatma-metni`}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {t('privacyPolicy')}
                  </Link>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors mt-0.5"
              aria-label="Banner'ı kapat"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
